import { getDataAPI, putDataAPI } from './api'
import { makeCacheKey, readSessionCache, writeSessionCache } from './sessionCache'

export const ASSET_PLATFORM_IDS = [
  'google-drive',
  'google-docs',
  'gmail',
  'dropbox',
  'onedrive',
  'icloud',
  'box',
  'notion',
  'figma',
  'slack',
  'spotify',
  'wetransfer',
  'other',
] as const

export type AssetPlatformId = (typeof ASSET_PLATFORM_IDS)[number]

const VALID_PLATFORMS = new Set<string>(ASSET_PLATFORM_IDS)

export function normalizeAssetPlatform(raw: unknown): AssetPlatformId {
  if (typeof raw === 'string' && VALID_PLATFORMS.has(raw)) return raw as AssetPlatformId
  return 'other'
}

export type DashboardAssetLink = {
  id: string
  name: string
  url: string
  platform: AssetPlatformId
}

/** Heuristic: treat as demo material for UI (Demo tag vs platform logo). */
export function dashboardAssetLinkLooksDemo(a: Pick<DashboardAssetLink, 'name' | 'url'>): boolean {
  if (/\bdemo\b/i.test(a.name)) return true
  if (/\bdemo\b/i.test(a.url)) return true
  return false
}

/** Session cache for dashboard Assets modal (same tab as Intelligence dashboard). No TTL — last fetched list until overwritten. */
export function dashboardAssetsSessionCacheKey(userId: string | null) {
  return makeCacheKey([userId, 'api', 'user/dashboard-assets'])
}

export function readCachedDashboardAssets(userId: string | null): DashboardAssetLink[] | null {
  return readSessionCache<DashboardAssetLink[]>(dashboardAssetsSessionCacheKey(userId))
}

export function writeCachedDashboardAssets(userId: string | null, links: DashboardAssetLink[]) {
  writeSessionCache(dashboardAssetsSessionCacheKey(userId), links)
}

const PREFIX = 'dizee:dashboardAssets:'

function storageKey(userId: string | null) {
  return `${PREFIX}${encodeURIComponent(userId ?? '__none__')}`
}

/** Parse `links` from GET/PUT `user/dashboard-assets` apiResponse body */
export function parseLinksFromApiPayload(payload: unknown): DashboardAssetLink[] {
  if (!payload || typeof payload !== 'object') return []
  const o = payload as Record<string, unknown>
  const inner = o.data !== undefined && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : o
  const links = inner.links
  if (!Array.isArray(links)) return []
  return links
    .filter(
      (x): x is { id: string; name: string; url: string; platform?: unknown } =>
        Boolean(x) &&
        typeof (x as { id?: unknown }).id === 'string' &&
        typeof (x as { name?: unknown }).name === 'string' &&
        typeof (x as { url?: unknown }).url === 'string'
    )
    .map((x) => ({
      id: x.id,
      name: x.name,
      url: x.url,
      platform: normalizeAssetPlatform(x.platform),
    }))
}

export async function fetchDashboardAssetLinksFromApi(): Promise<DashboardAssetLink[]> {
  const payload = await getDataAPI('user/dashboard-assets')
  return parseLinksFromApiPayload(payload)
}

export async function saveDashboardAssetLinksToApi(links: DashboardAssetLink[]): Promise<DashboardAssetLink[]> {
  const payload = await putDataAPI('user/dashboard-assets', { links })
  return parseLinksFromApiPayload(payload)
}

/** Legacy local-only cache (used as fallback + one-time migration) */
export function readDashboardAssetLinks(userId: string | null): DashboardAssetLink[] {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (x): x is { id: string; name: string; url: string; platform?: unknown } =>
          Boolean(x) &&
          typeof (x as { id?: unknown }).id === 'string' &&
          typeof (x as { name?: unknown }).name === 'string' &&
          typeof (x as { url?: unknown }).url === 'string'
      )
      .map((x) => ({
        id: x.id,
        name: x.name,
        url: x.url,
        platform: normalizeAssetPlatform(x.platform),
      }))
  } catch {
    return []
  }
}

export function writeDashboardAssetLinks(userId: string | null, items: DashboardAssetLink[]) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(items))
  } catch {
    /* ignore quota / private mode */
  }
}

export function normalizeAssetUrl(raw: string): string | null {
  const t = raw.trim()
  if (!t) return null
  try {
    return new URL(t).href
  } catch {
    try {
      return new URL(`https://${t}`).href
    } catch {
      return null
    }
  }
}

export function newAssetId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}
